import { ChatSettings, ChatbotChannelSettings, DigitalMapSettings } from 'models/settings';

export interface ChatLanguageContext {
  pathname: string;
  digitalMapId?: string;
  guestPortalId?: string;
  pageLanguage: string;
  browserLanguage?: string;
  // For root map pages, we need to resolve the digital map ID from custom domain paths
  mapPathId?: string; // The [id] from /maps/[id] route
  digitalMapSettings?: DigitalMapSettings; // Settings containing custom_domain_paths
}

/**
 * Extended context that supports server-side rendering
 */
export interface ChatLanguageContextSSR extends ChatLanguageContext {
  acceptLanguageHeader?: string;
}

export interface ChatLanguageResult {
  shouldUseBrowserLanguage: boolean;
  finalLanguage: string;
  needsRefetch: boolean;
}

/**
 * Determines the appropriate language for chat based on channel settings
 * This is the DRY version of the logic from ChatContext
 */
/**
 * Extracts browser language preference from Accept-Language header
 */
export function getBrowserLanguageFromHeader(acceptLanguageHeader?: string): string | undefined {
  if (!acceptLanguageHeader) return undefined;

  // Parse Accept-Language header (e.g., "en-US,en;q=0.9,ja;q=0.8")
  const languages = acceptLanguageHeader
    .split(',')
    .map((lang) => lang.split(';')[0].trim())
    .filter(Boolean);

  // Return the first (highest priority) language
  return languages[0] || undefined;
}

export function determineChatLanguage(
  chatSettings: ChatSettings | undefined,
  context: ChatLanguageContext | ChatLanguageContextSSR
): ChatLanguageResult {
  if (!chatSettings?.channel_settings) {
    return {
      shouldUseBrowserLanguage: false,
      finalLanguage: context.pageLanguage,
      needsRefetch: false,
    };
  }

  const channelSettings = chatSettings.channel_settings;
  let defaultLanguageSource = 'PAGE_LANGUAGE';
  const channelPageLanguage: string | undefined = context.pageLanguage;
  let channelsToCheck: ChatbotChannelSettings[] = [];
  let channelIdToMatch: string | undefined;

  // Determine which channel type to use based on the current page
  if (context.pathname.includes('/maps/')) {
    // Digital map pages - use actual digital map ID
    channelsToCheck = channelSettings?.map || [];

    // For root map pages (no apiKey in path), we may need to resolve the digital map ID
    if (
      !context.digitalMapId &&
      context.mapPathId &&
      context.digitalMapSettings?.custom_domain_paths
    ) {
      // Try to find the digital map ID from custom domain paths
      const customPath = context.digitalMapSettings.custom_domain_paths.find(
        (path) => path.path === context.mapPathId
      );
      channelIdToMatch = customPath?.digital_map_id || context.digitalMapId;
    } else {
      channelIdToMatch = context.digitalMapId;
    }
  } else if (context.pathname.includes('/guestportals')) {
    // Guest portal pages - use guest portal ID as channel_id
    channelsToCheck = channelSettings?.guest_portal || [];
    channelIdToMatch = context.guestPortalId;
  } else {
    // Booking site pages - use language code as channel_id
    channelsToCheck = channelSettings?.booking_site || [];
    // Convert language format from en-US to EN_US
    channelIdToMatch = context.pageLanguage.replace('-', '_').toUpperCase();
  }

  // Find matching channel by channel_id
  if (channelsToCheck.length > 0 && channelIdToMatch) {
    const matchingChannel = channelsToCheck.find(
      (channel) => channel.channel_id === channelIdToMatch
    );

    if (matchingChannel) {
      defaultLanguageSource = matchingChannel.default_language_source || 'PAGE_LANGUAGE';
    }
  }

  // Determine final language based on source
  const shouldUseBrowserLanguage = defaultLanguageSource === 'BROWSER_LANGUAGE';
  let finalLanguage = context.pageLanguage;

  if (shouldUseBrowserLanguage) {
    // Prefer explicit browser language, fall back to Accept-Language header
    const browserLang =
      context.browserLanguage ||
      ('acceptLanguageHeader' in context
        ? getBrowserLanguageFromHeader(context.acceptLanguageHeader)
        : undefined);

    if (browserLang) {
      finalLanguage = browserLang;
    }
  } else if (defaultLanguageSource === 'PAGE_LANGUAGE' && channelPageLanguage) {
    finalLanguage = channelPageLanguage;
  }

  // Check if we need to refetch settings with different language
  const needsRefetch = finalLanguage !== context.pageLanguage;

  return {
    shouldUseBrowserLanguage,
    finalLanguage,
    needsRefetch,
  };
}

/**
 * Converts language format to match API expectations
 */
export function normalizeLanguageForApi(language: string): string {
  // Convert from browser format (en-US) to API format if needed
  return language;
}

/**
 * Converts language from en-US format to EN_US format for channel matching
 */
export function normalizeLanguageForChannelId(language: string): string {
  return language.replace('-', '_').toUpperCase();
}
